import { useState, ChangeEvent, useEffect, FC } from "react";
import Image from "next/image";
import { useRouter } from "next/router";
import { useLocalStorage } from "@mantine/hooks";
import { StorageKeys } from "@components/Data/enums";
import { Spinner } from "@components/atoms";
import { apiCalls } from "apiCalls/apiCalls";
import { defaultCountry } from "@components/health-insurance-plan/summary/data";
import FlagExporter from "@components/atoms/Flags/FlagExporter";
import { Combobox } from "@headlessui/react";
import { ChevronDown } from "@components/SVGs";

interface SubmitValues {
    extraParameters?: any;
    [key: string]: any;
}

export function addExtraParameters(
    valuesToSubmit: SubmitValues,
    query: any
): SubmitValues {
    valuesToSubmit.extraParameters = {
        cid: query.cid || "",
        bc: query.bc || "511151",
        BL: query.BL || "NOC - NOIDA",
        AI: query.AI || "NA",
        AN: query.AN || "NA",
        RMC: query.RMC || "000P111042",
        RMN: query.RMN || "Ankur Kharbanda",
        C: query.C || "Online",
        AP: query.AP || "NA",
        PML: query.PML || "",
        utm_source: query.utm_source || "",
        utm_campaign: query.utm_campaign || "",
        utm_term: query.utm_term || "",
        utm_medium: query.utm_medium || "",
    };
    return valuesToSubmit;
}

const phoneRegex = /([0-9])\1{6,}/;
const validPhoneRegex = (min, max) => new RegExp(`^\\d{${min},${max}}$`);

const ViewPlanTravel = () => {
    const [error, setError] = useState(false);
    const [success, setSuccess] = useState(false);
    const [retry, setRetry] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState("");
    const [disableBtn, setDisableBtn] = useState(false);
    const [isLoading, setIsLoading] = useState(false); // Added loading state
    const [apiError, setApiError] = useState(""); // Added API error state

    const router = useRouter();

    const [travelForm, setTravelForm] = useLocalStorage<Record<string, any>>({
        key: StorageKeys.TRAVEL_FORM,
    });

    // Initialize phone number from localStorage
    useEffect(() => {
        if (travelForm?.mobileNumber) {
            setPhoneNumber(travelForm.mobileNumber);
        }
    }, []);

    const generateLead = async (mobileNumber: string) => {
        try {
            setIsLoading(true);
            setApiError("");
            setDisableBtn(true);
    
            const data = addExtraParameters({ ...travelForm, mobileNumber }, router.query);
    
            const response = await apiCalls.generateOnlineLeadForTravelV2(data);
    
            if (!response) {
                setApiError("Failed to generate lead. Try again.");
                setError(true);
                return;
            }
    
            setSuccess(true);
    
            await router.push("/travel-insurance.html");
         
        } catch (err) {
            console.error("API Error:", err);
            setApiError("Failed to generate lead. Please try again.");
            setError(true);
        } finally {
            setIsLoading(false);
            setDisableBtn(false);
        }
    };    

    const onMobileChange = async (e: ChangeEvent<HTMLInputElement>) => {
        const mobileNumber = e.target.value;
        const maxLength = 10;
        const minLength = 10;

        // Prevent typing more than 10 digits
        if (mobileNumber.length > maxLength) return;

        setPhoneNumber(mobileNumber);
        setApiError(""); // Clear any previous API errors

        // Update localStorage immediately
        setTravelForm((prev) => ({ ...prev, mobileNumber }));

        // Reset success if number changes
        if (mobileNumber.length !== maxLength && success) {
            setSuccess(false);
        }

        // Validate phone number
        if (
            phoneRegex.test(mobileNumber) ||
            ["1", "2", "3", "4"].includes(mobileNumber.charAt(0))
        ) {
            setError(true);
            return;
        }

        // Clear error if valid
        if (error) {
            setError(false);
        }

        // Check if we have a complete 10-digit number
        if (mobileNumber.length === maxLength) {
            if (validPhoneRegex(minLength, maxLength).test(mobileNumber)) {
                setDisableBtn(true);
                // Generate lead with the valid phone number
                await generateLead(mobileNumber);
            } else {
                setError(true);
            }
        }
    };

    const isDisabled = () => {
        return !validPhoneRegex(10, 10).test(phoneNumber) || isLoading || disableBtn;
    };

    useEffect(() => {
        setRetry(false);
    }, [error, success]);

    const startNowHandler = async () => {
        // Only proceed if we have a valid 10-digit number
        if (validPhoneRegex(10, 10).test(phoneNumber)) {
            setDisableBtn(true);
            await generateLead(phoneNumber);
        } else {
            setError(true);
        }
    };

    return (
        <div className="" id="travel-insurance-mobile-number-input">
            <div
                className={`flex flex-col gap-8 mt-5 bg-white items-center xs:justify-between justify-center w-full`}
                id="travel-insurance-mobile-number-input"
            >
                <div
                    className={`p-5 bg-white md:px-0 px-0 py-2 justify-between w-full relative rounded-md flex flex-col gap-3 border-2 ${error ? 'border-red-500' : 'border-gray-300'
                        }`}
                    id="travel-insurance-mobile-number-input"
                >
                    <>
                        <div className="flex gap-1 md:flex-row md:gap-0 md:p-2 relative justify-between mb-0 md:mb-0" id="travel-insurance-mobile-number-input">
                            <Combobox
                                value={defaultCountry}
                                disabled={true}
                            >
                                {({ value }) => (
                                    <>
                                        <div className="relative rounded-lg text-nivabupa-black" id="travel-insurance-mobile-country-dropdown">
                                            <div className="flex flex-row items-center bg-white" id="travel-insurance-mobile-country-dropdown">
                                                <div className="ml-2 flex flex-row items-center gap-1">
                                                    <FlagExporter countryName={value.country} />
                                                    <div id="country-code" className="py-2 pl-2 rounded-l-md">{`${value?.ISD}`}</div>
                                                </div>
                                                <ChevronDown className="ml-2 h-4 w-4 text-black" />
                                            </div>
                                        </div>
                                    </>
                                )}
                            </Combobox>

                            <div className={`flex flex-row md:gap-4 gap-1 items-center`} id="travel-insurance-mobile-number-input">
                                <div className="flex flex-row items-center bg-white p-3 max-w-[320px] rounded-sm relative" id="travel-insurance-mobile-number-input">
                                    <input
                                        id="travel-insurance-mobile-number-input"
                                        autoFocus
                                        className="w-full h-full placeholder:text-nivabupa-black placeholder:opacity-50 outline-none placeholder:font-normal placeholder:text-sm"
                                        type="tel"
                                        placeholder="Enter Mobile Number"
                                        required
                                        value={phoneNumber}
                                        onChange={(e) => {
                                            e.target.value = e.target.value.replace(/\D/g, "");
                                            onMobileChange(e);
                                        }}
                                        disabled={isLoading}
                                    />
                                </div>
                            </div>
                        </div>

                        {error && (
                            <span className="text-xs text-red-700" id="travel-insurance-mobile-number-input-error">
                                {apiError || "Please enter valid mobile number"}
                            </span>
                        )}
                    </>
                </div>

                <div
                    className={`flex flex-row justify-between w-[93px] items-center md:gap-3 gap-2`}
                    id="travel-insurance-start-now-btn"
                >
                    <button
                        className={`flex flex-row w-full items-center md:px-0 px-0 rounded-lg text-white ml-auto disabled:opacity-50 md:h-[54px] h-[39px] bg-nivabupa-ethinosHomeOrange`}
                        onClick={startNowHandler}
                        disabled={isDisabled()}
                        id="travel-insurance-start-now-btn"
                    >
                        <div className="h-full w-full justify-center flex flex-row items-center" id="travel-insurance-start-now-btn">
                            {isLoading ? (
                                <Spinner />
                            ) : (
                                <>
                                    <Image
                                        src="/ethinos-home-page/svgs/right-chevron.svg"
                                        width={10}
                                        height={15}
                                        alt="right chevron"
                                        className="hidden"
                                        id="travel-insurance-start-now-btn"
                                    />
                                    <span className="text-xs font-normal capitalize md:text-base md:flex" id="travel-insurance-start-now-btn">
                                        start now
                                    </span>
                                </>
                            )}
                        </div>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ViewPlanTravel;